import { Stack, StackItem, mergeStyleSets } from "@fluentui/react";
import { Link, scroller } from 'react-scroll';
import { mergeClasses } from "@fluentui/react-components";
import { FC, useEffect, useRef, useState } from "react";
import "./TabListStyles.scss";
import { commonStyles } from "../CommonStyles/CommonStyles";
import { useTranslation } from "react-i18next";
import { trackEvent } from '../../utilities/AppInsights';
import appInsights, { generateCookieId } from "../../utilities/AppInsights";

const styles = mergeStyleSets({
    navbarContainer: {
        position: "static",
        top: 0,
        "@media (max-width: 768px)": {
            display: "none",
        },
        zIndex: 100,
    },
    navbarContainerRtl: {
        position: "static",
        top: "0",
        zindex: "100",
        direction: "rtl",
        textAlign: "right"
    },
    navbar: {
        width: "100%",
        backgroundColor: "#fff",
        borderBottom: "1px solid  #DCDCDC",
    },
    noBorderRadiusClass: {
        borderTopRightRadius: "0px",
        borderTopLeftRadius: "0px",
    },
    fixed: {
        position: "fixed",
        top: 0,
        width: "100%",
        zIndex: 1000
    }
});

interface PageTabNavigatorProps {
    sections: { label: string; id: string; Componentname: string; heading?: string; topicHeading?: string }[];
}

const PageTabNavigator: FC<PageTabNavigatorProps> = ({ sections }) => {
    const { t } = useTranslation();
    const [isFixed, setIsFixed] = useState(false);

    const skills = t('Skills', { returnObjects: true }) as Array<{
        heading: string;
        description: string;
        skillsLink: string;
        assessment: string;
        imageUrl: string;
    }>;

    const firstHeadingValue = skills.length > 0 ? skills[0].heading : 'defaultHeading';

    const getValidTranslation = (section: any): string => {
        const primaryKey = `NavBar.${section.id}`;
        let rawTranslation = t(primaryKey, { defaultValue: '' });
        let translation = typeof rawTranslation === 'string' ? rawTranslation.trim() : '';

        const isInvalid = !translation || translation.toLowerCase() === 'null' || translation === primaryKey;

        if (!isInvalid) return translation;

        const fallbackKeys = [
            `${section.Componentname}.${section.Componentname}`,
            `${section.Componentname}.topicHeading`,
            `${section.Componentname}.heading`,
            `${section.id}.topicHeading`,
            `${section.Componentname}.BannerData.0.heading`,
            `${section.Componentname}.AIAssistantData.0.mainHeading`,

        ];

        for (const key of fallbackKeys) {
            if (!key) continue;
            rawTranslation = t(key, { defaultValue: '' });
            translation = typeof rawTranslation === 'string' ? rawTranslation.trim() : '';
            if (translation && translation.toLowerCase() !== 'null' && translation !== key) {
                return translation;
            }
        }

        // If all fallbacks failed and Componentname is 'Skills', use firstHeadingValue
        if (section.Componentname === 'Skills' && skills.length > 0) {
            return firstHeadingValue;
        }

        return 'Default'; // Final fallback
    };

    const initialActiveTab = sections.find((section) => {
        const label = getValidTranslation(section);
        return label && label.toLowerCase() !== 'default';
    })?.id || sections[0].id;

    const [activeTab, setActiveTab] = useState<string>(initialActiveTab);
    const [isAtTop, setIsAtTop] = useState<boolean>(true);

    const isRTL = document.body.classList.contains('langdirection-rtl');

    const navbarRef = useRef<HTMLDivElement>(null);
    const placeholderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleScroll = () => {
            const navbar = navbarRef.current;
            const placeholder = placeholderRef.current;
            if (navbar && placeholder) {
                const rect = placeholder.getBoundingClientRect();
                setIsFixed(rect.top <= 0 && window.scrollY > 0);
            }
            setIsAtTop(window.scrollY === 0);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const sections = document.querySelectorAll(".section");
            sections.forEach((section) => {
                const rect = section.getBoundingClientRect();
                if (rect.top <= 500 && rect.bottom >= 90) {
                    setActiveTab(section.id);
                }
            });
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleKeyDown = (event: any, sectionId: string) => {
        if (event.key === 'Enter') {
            scroller.scrollTo(sectionId, {
                duration: 600,
                delay: 0,
                smooth: 'easeInOutQuart',
                offset: -62,
            });
        }
    };

    return (
        <>
            <div ref={placeholderRef} style={{ height: isFixed ? navbarRef.current?.offsetHeight : 'auto' }} />
            <div
                className={mergeClasses(
                    styles.navbarContainer,
                    isFixed ? styles.fixed : '',
                    isAtTop ? styles.noBorderRadiusClass : ''
                )}
                id="navbar"
                ref={navbarRef}
            >
                <Stack>
                    <StackItem
                        align="center"
                        className={
                            isAtTop
                                ? mergeClasses(styles.navbar, styles.noBorderRadiusClass)
                                : styles.navbar
                        }
                    >
                        <Stack className={commonStyles.container}>
                            <nav className="tab-List">
                                {!!activeTab &&
                                    sections.map((section) => (
                                        <Link
                                            to={section.id}
                                            key={section.id}
                                            offset={-62}
                                            smooth={true}
                                            duration={600}
                                            tabIndex={0}
                                            className={
                                                activeTab === section.id
                                                    ? "nav-item is-active"
                                                    : "nav-item"
                                            }
                                            onKeyDown={(event) => handleKeyDown(event, section.id)}
                                            onClick={() => {
                                                scroller.scrollTo(section.id, {
                                                    duration: 600,
                                                    delay: 0,
                                                    smooth: 'easeInOutQuart',
                                                    offset: -62,
                                                });
                                                setTimeout(() => {
                                                    setActiveTab(section.id);
                                                }, 650);
                                                trackEvent('MenuNavigationClick', {
                                                    MenuNavigationClickText: section.label,
                                                    visitorId: generateCookieId(),
                                                });
                                            }}
                                        >
                                            {getValidTranslation(section)}
                                        </Link>
                                    ))}
                            </nav>
                        </Stack>
                    </StackItem>
                </Stack>
            </div>
        </>
    );
};

export default PageTabNavigator;
